import { useEffect, useState } from "react";
import { api } from "../lib/api";
import Layout from "../components/Layout";
import { Plus, Briefcase, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { brandify } from "../lib/brand";

const URGENCY = { "Critical":"bg-red-50 text-red-700 border-red-200","High":"bg-amber-50 text-amber-700 border-amber-200","Normal":"bg-slate-100 text-slate-700 border-slate-200","Low":"bg-slate-50 text-slate-500 border-slate-200" };
const SERVICE = { "StaffHub Direct":"bg-sky-50 text-sky-700 border-sky-200","StaffHub Managed":"bg-emerald-50 text-emerald-700 border-emerald-200","StaffHub AI":"bg-purple-50 text-purple-700 border-purple-200","StaffHub Hybrid":"bg-orange-50 text-orange-700 border-orange-200","StaffHub Teams":"bg-indigo-50 text-indigo-700 border-indigo-200" };

export default function JobOrders() {
  const [rows, setRows] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [meta, setMeta] = useState({});
  const [countries, setCountries] = useState([]);
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState("");
  const [form, setForm] = useState({
    company_id:"", role_title:"", industry:"",
    service_model:"StaffHub Direct", urgency:"Normal",
    seniority:"Mid-Level", years_experience:2, hours_per_week:40,
    compensation_min:1500, compensation_max:2000, compensation_period:"Monthly",
    us_equivalent_cost:6500, staffhub_solution_cost:2500,
    recommended_solution:"StaffHub Direct",
    location_type:"Global Remote",
    required_country:"", preferred_country:"",
    hard_country_requirement:false, hard_onsite_requirement:false, hard_timezone_requirement:false,
    required_timezone_overlap:"None",
    fee_model:"Fixed Placement Fee",
  });

  const load = async () => {
    const p = {}; if (status) p.status = status;
    const [{data:r},{data:c},{data:m},{data:g}] = await Promise.all([
      api.get("/job-orders",{params:p}),
      api.get("/companies"),
      api.get("/meta/lookups"),
      api.get("/geography/countries"),
    ]);
    setRows(r); setCompanies(c); setMeta(m); setCountries(g.countries || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {...form};
      // Auto-derive hard onsite from US Onsite location type
      if ((payload.location_type || "").toLowerCase().includes("onsite")) payload.hard_onsite_requirement = true;
      if (payload.location_type === "Specific Country" && !payload.required_country) {
        toast.error("Select a required country for Specific Country location type");
        return;
      }
      await api.post("/job-orders", payload);
      toast.success("Job order created");
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <Layout>
      <div className="mb-6 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-widest text-sky-600 mb-1">Job Orders</div>
          <h1 className="font-display text-3xl font-bold text-slate-900 tracking-tight">Workforce needs</h1>
          <p className="text-sm text-slate-500 mt-1">Every order asks — what work needs to get done — before who to hire.</p>
        </div>
        <button data-testid="new-job-btn" onClick={()=>setOpen(true)} className="h-10 px-4 rounded-md bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold inline-flex items-center gap-2"><Plus size={15}/> New job order</button>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="p-4 border-b border-slate-200 bg-slate-50/50 flex flex-wrap gap-3 items-center">
          <select data-testid="job-status-filter" value={status} onChange={e=>setStatus(e.target.value)} className="h-9 px-3 rounded-md border border-slate-200 text-sm bg-white">
            <option value="">All statuses</option>
            {(meta.job_statuses||[]).map(s=><option key={s}>{s}</option>)}
          </select>
          <div className="text-xs text-slate-500 ml-auto">{rows.length} orders</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-100/70 text-slate-600 text-[11px] uppercase tracking-widest font-semibold">
              <tr>
                <th className="text-left px-4 py-3">Role</th>
                <th className="text-left px-4 py-3">Client</th>
                <th className="text-left px-4 py-3">Service model</th>
                <th className="text-left px-4 py-3">Urgency</th>
                <th className="text-left px-4 py-3">Comp</th>
                <th className="text-left px-4 py-3">Est. savings/yr</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`job-row-${r.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-md bg-slate-100 text-slate-500 flex items-center justify-center"><Briefcase size={13}/></div>
                      <div>
                        <div className="font-semibold text-slate-900">{r.role_title}</div>
                        <div className="text-[11px] text-slate-500">{r.industry} · {r.hours_per_week}h/wk</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-700">{r.company_name || "—"}</td>
                  <td className="px-4 py-3"><span className={`inline-flex text-[10px] font-semibold px-2 py-0.5 rounded border ${SERVICE[r.service_model] || "bg-slate-100 text-slate-600 border-slate-200"}`}>{r.service_model}</span></td>
                  <td className="px-4 py-3"><span className={`inline-flex text-[10px] font-semibold px-2 py-0.5 rounded border ${URGENCY[r.urgency] || "bg-slate-100 text-slate-600 border-slate-200"}`}>{r.urgency}</span></td>
                  <td className="px-4 py-3 font-mono text-slate-700">${r.compensation_min?.toFixed(0)}–${r.compensation_max?.toFixed(0)}</td>
                  <td className="px-4 py-3 font-mono text-emerald-700 font-semibold">${(r.estimated_annual_savings || 0).toLocaleString()}</td>
                  <td className="px-4 py-3 text-[11px] text-slate-600">{r.status}</td>
                  <td className="px-4 py-3 text-right">
                    <Link to={`/matching?job=${r.id}`} data-testid={`match-btn-${r.id}`} className="inline-flex items-center gap-1 text-xs font-semibold text-sky-700 hover:text-sky-900"><Sparkles size={12}/> Match</Link>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={8} className="text-center py-12 text-sm text-slate-500">No job orders.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-6" onClick={()=>setOpen(false)}>
          <form onClick={e=>e.stopPropagation()} onSubmit={submit} data-testid="new-job-form" className="bg-white rounded-xl shadow-xl w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="font-display font-bold text-xl text-slate-900 mb-4">New job order</h2>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">Client company*</label>
                <select data-testid="jo-company" required value={form.company_id} onChange={e=>setForm({...form,company_id:e.target.value})} className="w-full h-9 px-3 rounded-md border border-slate-300 text-sm">
                  <option value="">Select company</option>
                  {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">Role title*</label>
                <input data-testid="jo-role" required value={form.role_title} onChange={e=>setForm({...form,role_title:e.target.value})} className="w-full h-9 px-3 rounded-md border border-slate-300 text-sm"/>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">Industry</label>
                <input value={form.industry} onChange={e=>setForm({...form,industry:e.target.value})} className="w-full h-9 px-3 rounded-md border border-slate-300 text-sm"/>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">Service model</label>
                <select value={form.service_model} onChange={e=>setForm({...form,service_model:e.target.value,recommended_solution:e.target.value})} className="w-full h-9 px-3 rounded-md border border-slate-300 text-sm">
                  {Object.keys(SERVICE).map(s=><option key={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">Urgency</label>
                <select value={form.urgency} onChange={e=>setForm({...form,urgency:e.target.value})} className="w-full h-9 px-3 rounded-md border border-slate-300 text-sm">
                  {["Low","Normal","High","Critical"].map(s=><option key={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">Years exp</label>
                <input type="number" value={form.years_experience} onChange={e=>setForm({...form,years_experience:parseInt(e.target.value||0)})} className="w-full h-9 px-3 rounded-md border border-slate-300 text-sm"/>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">Comp max/mo</label>
                <input type="number" value={form.compensation_max} onChange={e=>setForm({...form,compensation_max:parseFloat(e.target.value||0),compensation_min:parseFloat(e.target.value||0)*0.85})} className="w-full h-9 px-3 rounded-md border border-slate-300 text-sm"/>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">US equiv cost/mo</label>
                <input type="number" value={form.us_equivalent_cost} onChange={e=>setForm({...form,us_equivalent_cost:parseFloat(e.target.value||0)})} className="w-full h-9 px-3 rounded-md border border-slate-300 text-sm"/>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">ConnectMyHub cost/mo</label>
                <input type="number" value={form.staffhub_solution_cost} onChange={e=>setForm({...form,staffhub_solution_cost:parseFloat(e.target.value||0)})} className="w-full h-9 px-3 rounded-md border border-slate-300 text-sm"/>
              </div>

              <div className="col-span-2 border-t border-slate-200 pt-4 mt-2">
                <div className="text-[11px] font-semibold uppercase tracking-widest text-sky-600 mb-3">Geography & Location Requirements</div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">Seniority</label>
                <select data-testid="jo-seniority" value={form.seniority} onChange={e=>setForm({...form,seniority:e.target.value})} className="w-full h-9 px-3 rounded-md border border-slate-300 text-sm">
                  {(meta.seniorities||["Entry Level","Junior","Mid-Level","Senior","Manager","Director"]).map(s=><option key={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">Location model</label>
                <select data-testid="jo-location-type" value={form.location_type} onChange={e=>setForm({...form,location_type:e.target.value, hard_onsite_requirement: e.target.value.toLowerCase().includes("onsite")})} className="w-full h-9 px-3 rounded-md border border-slate-300 text-sm">
                  {(meta.location_types||["Worldwide / Best Qualified","U.S. Onsite","U.S. Hybrid","U.S. Remote","Nearshore","Global Remote","Specific Country","Any Location / Best Available"]).map(s=><option key={s}>{s}</option>)}
                </select>
              </div>

              {form.location_type === "Specific Country" && (
                <div className="col-span-2">
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">Required country*</label>
                  <select data-testid="jo-required-country" required value={form.required_country} onChange={e=>setForm({...form,required_country:e.target.value})} className="w-full h-9 px-3 rounded-md border border-slate-300 text-sm">
                    <option value="">Select required country</option>
                    {countries.map(c=><option key={c.iso2} value={c.name}>{c.name} ({c.region})</option>)}
                  </select>
                </div>
              )}

              <div className="col-span-2">
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">Preferred country <span className="font-normal text-slate-500">(preference only — not a quality proxy)</span></label>
                <select data-testid="jo-preferred-country" value={form.preferred_country} onChange={e=>setForm({...form,preferred_country:e.target.value})} className="w-full h-9 px-3 rounded-md border border-slate-300 text-sm">
                  <option value="">No preference</option>
                  {countries.map(c=><option key={c.iso2} value={c.name}>{c.name} ({c.region})</option>)}
                </select>
              </div>

              <div className="col-span-2 grid grid-cols-3 gap-3">
                <label className="flex items-center gap-2 px-3 py-2 rounded border border-slate-200 bg-slate-50 cursor-pointer">
                  <input data-testid="jo-hard-country" type="checkbox" checked={form.hard_country_requirement} onChange={e=>setForm({...form,hard_country_requirement:e.target.checked})}/>
                  <span className="text-xs">
                    <span className="font-semibold text-slate-800 block">Hard country requirement</span>
                    <span className="text-slate-500">Must match required country</span>
                  </span>
                </label>
                <label className="flex items-center gap-2 px-3 py-2 rounded border border-slate-200 bg-slate-50 cursor-pointer">
                  <input data-testid="jo-hard-onsite" type="checkbox" checked={form.hard_onsite_requirement} onChange={e=>setForm({...form,hard_onsite_requirement:e.target.checked})}/>
                  <span className="text-xs">
                    <span className="font-semibold text-slate-800 block">Hard onsite requirement</span>
                    <span className="text-slate-500">Must be in-country on-site</span>
                  </span>
                </label>
                <label className="flex items-center gap-2 px-3 py-2 rounded border border-slate-200 bg-slate-50 cursor-pointer">
                  <input data-testid="jo-hard-tz" type="checkbox" checked={form.hard_timezone_requirement} onChange={e=>setForm({...form,hard_timezone_requirement:e.target.checked})}/>
                  <span className="text-xs">
                    <span className="font-semibold text-slate-800 block">Hard time-zone requirement</span>
                    <span className="text-slate-500">Must work US business hours</span>
                  </span>
                </label>
              </div>

              <div className="col-span-2">
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">Required time-zone overlap</label>
                <select data-testid="jo-tz-overlap" value={form.required_timezone_overlap} onChange={e=>setForm({...form,required_timezone_overlap:e.target.value})} className="w-full h-9 px-3 rounded-md border border-slate-300 text-sm">
                  {["None","Partial overlap (≥ 4h)","Full US business hours","Full UK business hours","Full APAC business hours","Custom"].map(s=><option key={s}>{s}</option>)}
                </select>
              </div>

              <div className="col-span-2 p-3 rounded-md bg-slate-50 border border-slate-200 text-[11px] text-slate-600">
                <span className="font-semibold text-slate-800">Matching policy:</span> Country / nationality is never a quality proxy. A <span className="font-semibold">preference</span> only adds a small bonus and never penalizes. A <span className="font-semibold">hard requirement</span> caps non-matching candidates at 25% with an explicit reason. Work authorization for the required country satisfies a hard-country requirement even if the candidate is currently in another country.
              </div>
            </div>
            <div className="mt-4 p-3 rounded-md bg-emerald-50 border border-emerald-200 text-xs text-emerald-900">
              Estimated annual savings: <span className="font-mono font-bold">${(((form.us_equivalent_cost||0)-(form.staffhub_solution_cost||0))*12).toLocaleString()}</span>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button type="button" onClick={()=>setOpen(false)} className="h-9 px-4 rounded-md border border-slate-300 text-sm">Cancel</button>
              <button data-testid="jo-submit" type="submit" className="h-9 px-4 rounded-md bg-slate-900 text-white text-sm font-semibold">Create</button>
            </div>
          </form>
        </div>
      )}
    </Layout>
  );
}
